import { apiErrorCode, apiErrorMessage, formatWait, retryAfterSeconds } from './apiErrors';

/**
 * A comment request's failure as what the section does about it and the
 * sentence a person reads (keepr-api SCHEMA.md § Comments on items, "Who
 * may"). Pure; the section and its hook do the acting.
 *
 *   commentRefusal(error, 'add' | 'edit' | 'delete' | 'load' | 'history' | 'read', { migrated })
 *   → { kind, text }
 *
 * `migrated`: the comment the request was about is a note from before
 * comments (KPR-801), which only a manager deletes and nobody edits.
 *
 * `kind` tells the section how to carry on:
 *   - 'gone'     the item cannot be read any more (the API's own 404, no code)
 *                or is a record the caller reads only by reference: the whole
 *                section goes, quietly.
 *   - 'off'      the card stopped allowing comments (409 `comments_off`): the
 *                section goes, with its reason.
 *   - 'audit_changed'  a delete was sent under the strict-auditing mode the
 *                person saw, and the card's mode is not that now (409
 *                `comment_audit_changed`, nothing written). `strictAuditing`
 *                is the mode now, when the API says it: the section reloads
 *                and asks the person to confirm again under it.
 *   - 'refresh'  what is on screen is stale (the comment went, was deleted,
 *                the caller lost write): ask the list again.
 *                A `quiet` refusal (a delete's 404 `comment_not_found`) is not
 *                said at all.
 *   - 'retry'    nothing is wrong with the section; the press can be repeated.
 */
export const COMMENT_MAX_LENGTH = 20000;

const VERBS = {
	add: 'added',
	edit: 'saved',
	delete: 'deleted',
};

export function commentRefusal(error, action = 'add', { migrated = false } = {}) {
	if (!error?.response) return { kind: 'retry', text: apiErrorMessage(error) };
	const { status } = error.response;
	const data = error.response.data || {};
	const code = apiErrorCode(error);

	if (status === 429) {
		const wait = retryAfterSeconds(error);
		return {
			kind: 'retry',
			text: wait
				? `That is a lot of comments in a short time. Try again in ${formatWait(wait)}.`
				: 'That is a lot of comments in a short time. Try again in a little while.',
		};
	}
	if (status === 404 && code === 'comment_not_found') {
		// On a delete this can be the API's short undo window (a delete being
		// taken back reads as hidden for a moment): read the list again and
		// say nothing, the comment may be right back.
		return { kind: 'refresh', quiet: action === 'delete', text: 'That comment is not there any more.' };
	}
	if (status === 404) {
		return { kind: 'gone', text: 'This item is not there any more, or you can no longer open it.' };
	}
	switch (code) {
		case 'comments_off':
			return { kind: 'off', text: 'Comments are off for this card now.' };
		case 'comments_need_write':
			return { kind: 'refresh', text: 'You can read this item but not change it, so you cannot add, edit or delete comments.' };
		case 'collection_archived':
			return { kind: 'refresh', text: 'This collection is archived, so comments cannot be added, edited or deleted.' };
		case 'strict_auditing_required':
		case 'strict_auditing_invalid':
			// The delete's mode parameter was missing or malformed: a bug in this
			// client, never a state of the world. Say nothing specific.
			return { kind: 'retry', text: 'The comment could not be deleted. Try again.' };
		case 'comment_audit_changed':
			return {
				kind: 'audit_changed',
				strictAuditing: typeof data.strictAuditing === 'boolean' ? data.strictAuditing : null,
				text: 'Strict comment auditing was changed for this card since you last looked, so nothing was deleted.',
			};
		case 'comment_not_author':
			return {
				kind: 'refresh',
				text: migrated
					? 'Only a manager of this collection can delete a note from before comments.'
					: 'Only the person who wrote a comment can edit or delete it.',
			};
		case 'comment_migrated':
			return { kind: 'refresh', text: 'A note from before comments cannot be edited.' };
		case 'comment_deleted':
			return { kind: 'refresh', text: 'That comment has been deleted.' };
		case 'comment_edit_limit': {
			const limit = Number(data.limit);
			return {
				kind: 'retry',
				text: Number.isFinite(limit) && limit > 0
					? `A comment can be edited ${limit.toLocaleString()} times, and this one has been. Add a new comment instead.`
					: 'This comment has been edited as many times as it can be. Add a new comment instead.',
			};
		}
		case 'comment_conflict':
			return { kind: 'retry', text: 'Another change to your comment landed first. Try again.' };
		case 'comment_blank':
			return { kind: 'retry', text: 'Write something first.' };
		case 'comment_too_long': {
			const limit = Number(data.limit) || COMMENT_MAX_LENGTH;
			const length = Number(data.length);
			return {
				kind: 'retry',
				text: Number.isFinite(length) && length > 0
					? `That comment is ${length.toLocaleString()} characters long, and the most is ${limit.toLocaleString()}.`
					: `A comment can be up to ${limit.toLocaleString()} characters.`,
			};
		}
		case 'comment_invalid':
			return { kind: 'retry', text: 'That comment could not be read. Try again.' };
		default:
			return {
				kind: 'retry',
				text: apiErrorMessage(error, VERBS[action] ? `The comment could not be ${VERBS[action]}. Try again.` : 'Try again in a moment.'),
			};
	}
}
